import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { PrimaryButton, SecondaryButton } from '@ui/buttons';
import Checkbox from '@ui/checkbox';
import { FormInput } from '@ui/forms';
import { CloseIcon } from '@ui/icons';
import { Radio } from '@ui/radio';
import { useState } from 'react';
import Styled from './QuestionPanel.styled';

const FREE_TEXT_OPTION = 'Something else...';

type QuestionAnswer = {
  question: string;
  answers: string[];
  label?: string;
};

function isConfirmAudienceLabel(label?: string): boolean {
  return Boolean(label?.startsWith('Confirm') && label.endsWith('audience'));
}

function formatAnswer(answer: string, label?: string): string {
  if (isConfirmAudienceLabel(label)) {
    const separatorIndex = answer.indexOf(' - ');
    return separatorIndex === -1 ? answer : answer.slice(0, separatorIndex);
  }

  return answer;
}

function formatCollectedAnswers(items: QuestionAnswer[]): string {
  return items
    .map(({ question, answers, label }) => {
      const list = answers
        .map((answer) => formatAnswer(answer, label))
        .join(', ')
        .replace(/,([^,]*)$/, ' and$1');
      return `Q: ${question}\nA: ${list}`;
    })
    .join('\n\n');
}

interface Props {
  onSubmit: (answer: string) => void;
}

function QuestionPanel({ onSubmit }: Props) {
  const questions = useChatStore((store) => store.questionPanelData);
  const setIsQuestionPanelOpen = useShellStore(
    (store) => store.setIsQuestionPanelOpen
  );

  const [index, setIndex] = useState(0);
  const [collected, setCollected] = useState<QuestionAnswer[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');

  const { prompt, options, selectionMode, allowSkip, label } = questions[index];

  const isMulti = selectionMode === 'multi';
  const isLastQuestion = index === questions.length - 1;
  const progress = `${index + 1} of ${questions.length}`;
  const isFreeText = !isMulti && selection.includes(FREE_TEXT_OPTION);
  const answers = isFreeText ? [freeText.trim()] : selection;

  const canSubmit = isFreeText
    ? freeText.trim().length > 0
    : selection.length > 0;

  const hint = isMulti
    ? `${selection.length} selected`
    : isFreeText
      ? freeText.trim() || 'Describe your answer'
      : (selection[0] ?? 'Select an option');

  const closePanel = () => {
    const omitted = questions.slice(index).map((q) => ({
      question: q.prompt,
      answers: ['Not sure'],
      label: q.label
    }));

    setIsQuestionPanelOpen(false);
    onSubmit(formatCollectedAnswers([...collected, ...omitted]));
  };

  const advance = (skip?: boolean) => {
    const newCollected = [
      ...collected,
      {
        question: prompt,
        answers: skip ? ['Not sure'] : answers,
        label
      }
    ];

    if (!isLastQuestion) {
      setCollected(newCollected);
      setIndex(index + 1);
      setSelection([]);
      setFreeText('');
      return;
    }

    onSubmit(formatCollectedAnswers(newCollected));
    setIsQuestionPanelOpen(false);
  };

  const handleSingleSelect = (option: string) => {
    setSelection([option]);
    setFreeText('');
  };

  const handleMultiToggle = (option: string) => {
    setSelection((prev) =>
      prev.includes(option)
        ? prev.filter((o) => o !== option)
        : [...prev, option]
    );
  };

  return (
    <Styled.Panel>
      <Styled.Header>
        <Styled.HeaderLabel>{label ?? 'One quick question'}</Styled.HeaderLabel>
        {questions.length > 1 && <Styled.Progress>{progress}</Styled.Progress>}
        <Styled.CloseButton
          type="button"
          aria-label="Close question panel"
          onClick={closePanel}>
          <CloseIcon />
        </Styled.CloseButton>
      </Styled.Header>
      <Styled.Body>
        <Styled.Question>{prompt}</Styled.Question>
        <Styled.Options>
          {options
            .filter((option) => !(isFreeText && option === FREE_TEXT_OPTION))
            .map((option) =>
              isMulti ? (
                <Styled.Option key={option}>
                  <Checkbox
                    label={option}
                    state={selection.includes(option) ? 'checked' : 'unchecked'}
                    onClick={() => handleMultiToggle(option)}
                  />
                </Styled.Option>
              ) : (
                <Styled.Option key={option}>
                  <Radio
                    label={option}
                    value={option}
                    checked={selection[0] === option}
                    onChange={() => handleSingleSelect(option)}
                  />
                </Styled.Option>
              )
            )}
        </Styled.Options>
        {isFreeText && (
          <FormInput
            autoFocus
            fullWidth
            placeholder="Please describe..."
            onChange={(e) => setFreeText(e.target.value)}
          />
        )}
      </Styled.Body>
      <Styled.Footer>
        <Styled.Hint>{hint}</Styled.Hint>
        <Styled.Actions>
          {allowSkip && (
            <SecondaryButton onClick={() => advance(true)}>
              Skip
            </SecondaryButton>
          )}
          <PrimaryButton disabled={!canSubmit} onClick={() => advance()}>
            {isLastQuestion ? 'Submit' : 'Next'}
          </PrimaryButton>
        </Styled.Actions>
      </Styled.Footer>
    </Styled.Panel>
  );
}

export default QuestionPanel;
